import React, { useRef, useState, useEffect } from "react";
// import MDOjoma from "../assets/images/md-ojoma-image.png";
import MDOjoma from "../assets/images/Ojoma_Pic_Edited_03.png";
import MapOfAfrica from "../assets/images/map-of-africa-with-locations.svg";

const MDMessageSection: React.FC = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(30); // percentage of the track

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;
      const totalScrollable = scrollHeight - clientHeight;
      if (totalScrollable > 0) {
        setScrollProgress(scrollTop / totalScrollable);
      }
    }
  };

  // Allow clicking the track to jump to a position in the content
  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const thumbRef = React.useRef<HTMLDivElement | null>(null);
  const isDraggingRef = React.useRef(false);

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    const container = scrollContainerRef.current;
    if (!track || !container) return;
    const rect = track.getBoundingClientRect();
    const clickY = e.clientY - rect.top; // px from top of track
    const clickRatio = clickY / rect.height;
    const { scrollHeight, clientHeight } = container;
    const totalScrollable = scrollHeight - clientHeight;
    container.scrollTop = Math.round(clickRatio * totalScrollable);
    // update state immediately
    setScrollProgress(clickRatio);
  };

  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.userSelect = "none";
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    isDraggingRef.current = true;
    document.body.style.userSelect = "none";
    e.preventDefault();
  };

  const handleReadMoreClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const footerCTA = document.getElementById("footer-cta");
    if (footerCTA) {
      footerCTA.scrollIntoView({ behavior: "smooth" });
    }
  };

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const track = trackRef.current;
      const container = scrollContainerRef.current;
      if (!track || !container) return;
      const rect = track.getBoundingClientRect();
      let clientY = e.clientY;
      let clickY = clientY - rect.top;
      clickY = Math.max(0, Math.min(clickY, rect.height));
      const clickRatio = clickY / rect.height;
      const totalScrollable = container.scrollHeight - container.clientHeight;
      container.scrollTop = Math.round(clickRatio * totalScrollable);
      setScrollProgress(clickRatio);
    };

    const onUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        document.body.style.userSelect = "";
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isDraggingRef.current) return;
      const track = trackRef.current;
      const container = scrollContainerRef.current;
      if (!track || !container) return;
      const rect = track.getBoundingClientRect();
      const clientY = e.touches[0].clientY;
      let clickY = clientY - rect.top;
      clickY = Math.max(0, Math.min(clickY, rect.height));
      const clickRatio = clickY / rect.height;
      const totalScrollable = container.scrollHeight - container.clientHeight;
      container.scrollTop = Math.round(clickRatio * totalScrollable);
      setScrollProgress(clickRatio);
      e.preventDefault();
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  useEffect(() => {
    const updateDimensions = () => {
      if (scrollContainerRef.current) {
        const { scrollHeight, clientHeight } = scrollContainerRef.current;
        // Calculate what percentage of the total content is visible
        const visibleRatio = clientHeight / scrollHeight;
        setThumbHeight(Math.max(visibleRatio * 100, 10)); // Min 10% height for the thumb
      }
    };

    updateDimensions();
    // Use a small timeout to ensure content is rendered
    const timer = setTimeout(updateDimensions, 100);

    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
      clearTimeout(timer);
    };
  }, []);

  // Calculate the top offset for the thumb: progress * (trackHeight - thumbHeight)
  const thumbPosition = scrollProgress * (100 - thumbHeight);

  return (
    <section id="md-message-section">
      <div className="md-message-top">
        <div className="md-content-wrapper">
          <div className="md-text-side">
            <h2>
              Message from the <br />
              <span className="red-highlight">Managing Director</span>
            </h2>

            <div className="md-custom-scrollbar-area">
              {/* Custom UI Visual Scrollbar (Yellow Bar) */}
              <div
                className="md-ui-scrollbar-track"
                ref={trackRef}
                onClick={handleTrackClick}
              >
                <div
                  ref={thumbRef}
                  className="md-ui-scrollbar-thumb"
                  style={{
                    height: `${thumbHeight}%`,
                    top: `${thumbPosition}%`,
                  }}
                  onMouseDown={handleThumbMouseDown}
                  onTouchStart={handleTouchStart}
                />
              </div>

              {/* The Actual Scrollable Content */}
              <div
                className="md-scroll-content-container"
                ref={scrollContainerRef}
                onScroll={handleScroll}
              >
                <div className="md-text-content">
                  <p>
                    Africa prospers when education outcomes improve, when people
                    can access quality healthcare, and when livelihoods become
                    more resilient. That is why CcHUB exists, and why our
                    current strategy is focused on supporting founders,
                    creators, teams, and the ecosystems around three focus
                    sectors: Education, Health, and Creative Industries,
                    leveraging technology and social capital to transform
                    outcomes for millions of Africans.
                  </p>
                  <p>
                    Supporting innovators and entrepreneurs across 49 countries
                    in 2025, we pursued this strategy using eight levers that
                    help good ideas become real-world systems of change:
                    Academic/Research-based Innovation Civic & Digital
                    Resilience; Community & Ecosystems; Innovation Hub
                    Strengthening; Investment; Research & Human-Centred Design;
                    Support for Startups (including mainstream AI, emerging
                    tech, and green/sustainable technologies) and Workforce
                    Development. These levers are how we build the conditions
                    for innovation to compound - beyond any single program or
                    cohort.
                  </p>
                </div>
              </div>
            </div>

            <a
              href="#footer-cta"
              onClick={handleReadMoreClick}
              className="read-more-link"
            >
              Read more in the report <span className="arrow-down">▼</span>
            </a>
          </div>
          <div className="md-image-side">
            <img src={MDOjoma} alt="Ojoma Ochai - Managing Director" />
          </div>
        </div>
      </div>

      <div className="map-transition">
        <div className="impact-multiplier-badge">
          <div className="multiplier-number">5x</div>
          <div className="multiplier-text">
            For every dollar we invested, our <br />
            startups raised an additional 5X <br />
            in outside funding
          </div>
        </div>
      </div>
      <div className="md-message-bottom-map">
        <div className="map-container">
          <div className="map-image-wrapper">
            <img src={MapOfAfrica} alt="Map of Africa" />
          </div>
          <div className="map-info">
            <h3>
              Six hubs. Forty-nine <br /> countries.
            </h3>
            <p>
              Each innovation hub responds to what the local ecosystem actually
              needs.
            </p>
            <div className="hub-locations-grid">
              <div className="hub-location">
                <span className="location-dot-wrap">
                  <span className="location-dot"></span>
                </span>
                Lagos
              </div>
              <div className="hub-location">
                <span className="location-dot-wrap">
                  <span className="location-dot"></span>
                </span>
                Nairobi
              </div>
              <div className="hub-location">
                <span className="location-dot-wrap">
                  <span className="location-dot"></span>
                </span>
                Kigali
              </div>
              <div className="hub-location">
                <span className="location-dot-wrap">
                  <span className="location-dot"></span>
                </span>
                Windhoek
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MDMessageSection;
